import type { TFunction } from "i18next"

import type { ShipVariantOption } from "@/types/missions"

/**
 * OKS-987 — the one place a `ProductVariant` becomes a human string.
 *
 * # ⚠️ Shared by the queue row and the ship dialog's `<option>`, deliberately
 *
 * The dialog composed this inline and the row needed the same string. Two
 * compositions of one fact drift silently: the row would say "Noir" while the
 * select offered "Noir · SKU-12 — archived", and nothing on either side
 * compares them. This ticket exists to stop the row and the dialog disagreeing
 * about which variant ships, so a second composition would reintroduce exactly
 * the ambiguity it removes.
 *
 * # ⚠️ An empty title is a REAL shape, not a hypothetical
 *
 * `product_variants.title` is `NOT NULL DEFAULT ''`, so a variant with no title
 * is constructible and reachable — a manual variant written with only a SKU is
 * the ordinary case. `[title, sku]` both blank therefore falls back to a named
 * string rather than rendering an empty label, which would read as a rendering
 * bug rather than as missing data.
 *
 * # Archived variants are MARKED, not hidden
 *
 * `MissionProducts::ShipVariantSet` includes archived variants on purpose — it
 * describes what IS shipping, not what MAY be gifted (that inversion is
 * OKS-934 PRD §3). The marker is TEXT, not a badge: a native `<select>` renders
 * option content as plain text, so no component can live inside one, and the
 * row uses the same string so the two cannot diverge.
 *
 * # ⚠️ `sku: false` — de-densifying MULTI-ENTRY lists only (PO, 82)
 *
 * A `pinned_choice` sentence stacks three delimiter types on one line: ` · `
 * inside each label, `, ` between them, ` — ` before an archived marker. On the
 * line a packer scans, that reads as noise rather than as structure. So a list
 * of several variants drops the SKU and keeps the titles.
 *
 * Where the SKU stays, deliberately: the ship dialog's `<option>`s (that is the
 * pick-time lookup, where the SKU is the thing you match against the shelf) and
 * every SINGLE-variant statement (`fixed`), which has no delimiter pile-up and
 * where the SKU is free identity.
 *
 * ⚠️ ONE helper with a flag, not a second function. The two forms differ only in
 * this field; splitting them would let the archived marker or the `unnamed`
 * fallback drift between the row and the select, which is the exact divergence
 * this helper exists to prevent.
 *
 * ⚠️ The SKU still wins over an EMPTY TITLE even in list form. `title` is
 * `NOT NULL DEFAULT ''`, so a SKU-only variant is a real row, and there the SKU
 * *is* the identity — dropping it would render "Unnamed variant" for a variant
 * we can perfectly well name. `sku: false` means "prefer the title", not
 * "discard the SKU".
 *
 * @param option the wire shape both surfaces receive
 * @param t react-i18next's `t`, passed in rather than hooked so this stays a
 *   pure function callable from inside `.map()` and from non-component code
 * @param sku include the SKU alongside the title. `false` in multi-entry lists.
 * @returns a display string, never empty
 */
export function variantLabel(
  option: ShipVariantOption,
  t: TFunction,
  { sku = true }: { sku?: boolean } = {},
): string {
  const parts =
    sku || !option.title ? [option.title, option.sku] : [option.title]
  const base = parts.filter(Boolean).join(" · ")
  const label = base || t("collabs.ship.variant.unnamed")

  return option.archived
    ? `${label} — ${t("collabs.ship.variant.archived")}`
    : label
}
